import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Typography, Card, CardContent, Chip } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Download, Home, Dashboard } from '@mui/icons-material';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { width, height } = useWindowSize();
  const { user, updateUser } = useAuth();
  
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const paymentId = searchParams.get('payment_id');
        const orderId = searchParams.get('order_id');
        const creditsAdded = searchParams.get('credits_added');
        const packageName = searchParams.get('package_name');

        if (paymentId || orderId) {
          // Try to get detailed payment information
          try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('token');
            const response = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/payments/${paymentId || orderId}`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            
            if (response.data.success) {
              setPaymentData(response.data.data);
              if (response.data.data.invoice) {
                setInvoiceData(response.data.data.invoice);
              }
            }
          } catch (err) {
            console.log('Could not fetch detailed payment info, using URL params');
          }
        }

        // Set basic data from URL parameters
        setPaymentData({
          creditsAdded: creditsAdded || 'Unknown',
          packageName: packageName || 'Credit Package',
          paymentId: paymentId,
          orderId: orderId,
          timestamp: new Date().toLocaleString()
        });

        // Update user data to reflect new credits
        if (updateUser) {
          await updateUser();
        }

      } catch (error) {
        console.error('Error fetching payment details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [searchParams, updateUser]);

  const downloadInvoice = async () => {
    if (!invoiceData?.downloadUrl) {
      console.error('No invoice download URL available');
      return;
    }

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await axios.get(invoiceData.downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceData.invoiceNumber || paymentData?.paymentId || 'receipt'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Invoice download failed:', error);
      alert('Failed to download invoice. Please try again later.');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6">Loading payment details...</Typography>
      </Container>
    );
  }

  return (
    <>
      <Confetti 
        width={width} 
        height={height} 
        recycle={false}
        numberOfPieces={200}
        gravity={0.2}
      />
      
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <CheckCircle 
            sx={{ 
              fontSize: 80, 
              color: 'success.main',
              mb: 2
            }} 
          />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: 'success.main' }}>
            Payment Successful!
          </Typography>
          <Typography variant="h5" gutterBottom sx={{ color: 'text.secondary' }}>
            Thank you for your purchase!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
            Your credits have been successfully added to your account. 
            You're now ready to unlock the full potential of Quantum ROI Analyzer.
          </Typography>
        </Box>

        {/* Payment Details Card */}
        <Card sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Order Summary
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Package:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {paymentData?.packageName}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Credits Added:
              </Typography>
              <Chip 
                label={`+${paymentData?.creditsAdded} credits`} 
                color="success" 
                size="small"
              />
            </Box>
            
            {paymentData?.paymentId && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Payment ID:
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {paymentData.paymentId.slice(-8)}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Date:
              </Typography>
              <Typography variant="body2">
                {paymentData?.timestamp}
              </Typography>
            </Box>

            {/* Invoice Download */}
            {(invoiceData || paymentData?.paymentId) && (
              <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={downloadInvoice}
                  fullWidth
                >
                  Download Receipt
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/app')}
            startIcon={<Dashboard />}
            sx={{ px: 4 }}
          >
            Start Calculating ROI
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/dashboard')}
          >
            View Dashboard
          </Button>
          <Button
            variant="text"
            size="large"
            onClick={() => navigate('/')}
            startIcon={<Home />}
          >
            Back to Home
          </Button>
        </Box>

        {/* Current Balance Display */}
        {user && (
          <Box sx={{ mt: 4, p: 3, bgcolor: 'success.50', borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="body1" fontWeight="medium">
              Your current balance: <strong>{user.credits} credits</strong>
            </Typography>
          </Box>
        )}
      </Container>
    </>
  );
};

export default PaymentSuccess;